import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, ArrowUpRight, Home, Info, Layers, Briefcase, Handshake, Mail } from 'lucide-react';
import { navigationLinks, subsidiaries } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';
import LanguageToggle from '../ui/LanguageToggle';
import SubsidiaryIcon from '../ui/SubsidiaryIcon';

const NAV_ICONS = { Home, Info, Layers, Briefcase, Handshake, Mail };

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [ecosystemOpen, setEcosystemOpen] = useState(false);
  const location = useLocation();
  const { language, t } = useLanguage();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1c1b]/95 backdrop-blur-sm border-b border-[rgba(91,122,138,0.2)]">
      <div className="container-swg">
        <div className="flex items-center justify-between h-20">
          <Link to="/home" className="flex items-center gap-4 group" data-testid="header-logo-link">
            <img
              src="/logos/swg-icon.png"
              alt="Smartworld Group"
              className="h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105 drop-shadow-[0_0_20px_rgba(91,122,138,0.35)]"
              data-testid="header-logo-img"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-2xl font-black tracking-tight text-white">SWG</span>
              <span className="text-[10px] font-medium tracking-[0.25em] uppercase text-[#9AACB8] mt-0.5">Smartworld Group</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navigationLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                data-testid={`nav-link-${link.key}`}
              >
                {t(`nav.${link.key}`)}
              </Link>
            ))}

            <div className="relative">
              <button
                onClick={() => setEcosystemOpen(!ecosystemOpen)}
                onBlur={() => setTimeout(() => setEcosystemOpen(false), 150)}
                className="nav-link flex items-center gap-1"
                data-testid="nav-companies-toggle"
              >
                {t('nav.companies')}
                <ChevronDown className={`w-4 h-4 transition-transform ${ecosystemOpen ? 'rotate-180' : ''}`} />
              </button>

              {ecosystemOpen && (
                <div
                  className="absolute top-full right-0 mt-3 w-[640px] bg-[#1a1c1b] border border-[rgba(91,122,138,0.4)] rounded-2xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.7)] p-3 z-50"
                  data-testid="companies-dropdown"
                >
                  <div className="px-3 pt-2 pb-3 flex items-center justify-between border-b border-[rgba(91,122,138,0.2)] mb-2">
                    <div>
                      <p className="text-[10px] tracking-[0.25em] uppercase text-[#5B7A8A] font-semibold">{t('home.ourPortfolio')}</p>
                      <p className="text-[#9AACB8] text-xs mt-0.5">{t('home.ourPortfolioSub')}</p>
                    </div>
                    <Link
                      to="/ecosystem"
                      onClick={() => setEcosystemOpen(false)}
                      onMouseDown={(e) => e.preventDefault()}
                      className="text-[10px] tracking-[0.2em] uppercase text-[#5B7A8A] font-semibold flex items-center gap-1 hover:text-white transition-colors"
                    >
                      {t('cta.viewAll')} <ArrowUpRight className="w-3 h-3" />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {subsidiaries.map((sub) => (
                      <Link
                        key={sub.id}
                        to={`/subsidiary/${sub.id}`}
                        onClick={() => setEcosystemOpen(false)}
                        onMouseDown={(e) => e.preventDefault()}
                        className="group flex items-center gap-3 p-3 rounded-xl hover:bg-[rgba(91,122,138,0.12)] transition-colors"
                        data-testid={`companies-dropdown-${sub.id}`}
                      >
                        <SubsidiaryIcon subsidiary={sub} size="md" />
                        <div className="min-w-0 flex-1">
                          <div className="text-white text-sm font-semibold group-hover:text-[#5B7A8A] transition-colors">{sub.name}</div>
                          <div className="text-[#9AACB8] text-[11px] mt-0.5 truncate" style={{ color: 'rgba(154,172,184,0.7)' }}>{sub.i18n[language].tagline}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <LanguageToggle />
            <Link to="/partnerships" className="btn-primary" data-testid="header-partner-btn">
              {t('cta.partnerWithUs')}
            </Link>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden py-6 border-t border-[rgba(91,122,138,0.2)]" data-testid="mobile-menu">
            <nav className="flex flex-col gap-1">
              {navigationLinks.map((link) => {
                const Icon = NAV_ICONS[link.icon] || Home;
                const active = isActive(link.path);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="group relative flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200"
                    style={{
                      background: active ? 'rgba(91,122,138,0.15)' : 'transparent'
                    }}
                    data-testid={`mobile-nav-${link.key}`}
                  >
                    {/* Active left accent bar */}
                    <span
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-full transition-all duration-300"
                      style={{
                        height: active ? '60%' : '0%',
                        background: '#5B7A8A'
                      }}
                    />

                    {/* Icon badge */}
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-200"
                      style={{
                        background: active ? 'rgba(91,122,138,0.25)' : 'rgba(91,122,138,0.08)',
                        border: `1px solid ${active ? 'rgba(91,122,138,0.5)' : 'rgba(91,122,138,0.2)'}`
                      }}
                    >
                      <Icon className="w-4 h-4" style={{ color: active ? '#fff' : '#5B7A8A' }} />
                    </div>

                    {/* Label */}
                    <span
                      className="flex-1 text-base font-medium transition-colors duration-200"
                      style={{ color: active ? '#fff' : '#e5e7eb' }}
                    >
                      {t(`nav.${link.key}`)}
                    </span>

                    {/* Chevron */}
                    <ChevronRight
                      className="w-4 h-4 transition-all duration-200 group-hover:translate-x-1"
                      style={{ color: active ? '#5B7A8A' : 'rgba(154,172,184,0.5)' }}
                    />
                  </Link>
                );
              })}

              {/* COMPANIES list removed — Ecosystem link handles this */}

              <div className="px-4 mt-6 flex flex-col gap-3">
                <LanguageToggle />
                <Link to="/partnerships" onClick={() => setMobileMenuOpen(false)} className="btn-primary w-full justify-center">
                  {t('cta.partnerWithUs')}
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
